<template>
  <div class="max-w-2xl mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
    <div class="flex items-center justify-between">
      <button @click="goBack" class="btn btn-secondary !p-2 !rounded-full">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h1 class="text-xl font-bold text-slate-800 dark:text-slate-100">
        {{ isEditing ? 'Editar Grupo' : 'Criar Grupo' }}
      </h1>
      <button @click="handleSubmit" :disabled="isSubmitting" class="btn btn-primary min-w-[80px]">
        <span v-if="isSubmitting" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
        <span v-else>Salvar</span>
      </button>
    </div>

    <div class="panel p-5 sm:p-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <div>
          <label for="pNome" class="form-label">Nome do Serviço</label>
          <select id="pNome" v-model="form.nomeStreamer" @change="handleServiceSelection(form)" required
            class="form-select">
            <option disabled value="">Selecione um serviço...</option>
            <option v-for="service in availableServices" :key="service.key" :value="service.displayName">
              {{ service.displayName }}
            </option>
          </select>
        </div>
        <div>
          <label for="pPlataforma" class="form-label">Plataforma</label>
          <input id="pPlataforma" v-model="form.plataforma" type="text" required
            placeholder="Selecione um serviço para preencher" class="form-input bg-slate-100 dark:bg-slate-700/50"
            readonly />
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-6">
          <div>
            <label for="pValorMensal" class="form-label">Valor Mensal (R$)</label>
            <input id="pValorMensal" v-model.number="form.valorMensal" type="number" step="0.01" required
              placeholder="29.90" class="form-input" />
          </div>
          <div>
            <label for="pDiaVencimento" class="form-label">Dia do Vencimento</label>
            <input id="pDiaVencimento" v-model.number="form.diaVencimento" type="number" required min="1" max="31"
              placeholder="15" class="form-input" />
          </div>
        </div>
        <div v-if="!isEditing">
          <label for="pEmails" class="form-label">Convidar Membros (opcional)</label>
          <textarea id="pEmails" v-model="form.emailsMembros" rows="3"
            placeholder="Separe os emails por vírgula ou espaço" class="form-input"></textarea>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import axios from 'axios';

const router = useRouter();
const route = useRoute();

const isEditing = computed(() => !!route.params.uuid);
const isSubmitting = ref(false);

const form = ref({
  nomeStreamer: '',
  plataforma: '',
  valorMensal: null,
  diaVencimento: null,
  emailsMembros: ''
});

const API_BASE_URL = 'https://backend.controlease.com.br/api';
const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const platformStyles = {
    'default': { displayName: 'Outro', plataforma: 'Serviço' },
    'netflix': { displayName: 'Netflix', plataforma: 'Streaming de Vídeo' },
    'spotify': { displayName: 'Spotify', plataforma: 'Streaming de Música' },
    'twitch': { displayName: 'Twitch', plataforma: 'Streaming de Vídeo'},
    'youtube': { displayName: 'YouTube', plataforma: 'Streaming de Vídeo'},
    'prime video': { displayName: 'Prime Video', plataforma: 'Streaming de Vídeo'},
    'disney+': { displayName: 'Disney+', plataforma: 'Streaming de Vídeo'},
    'max': { displayName: 'Max', plataforma: 'Streaming de Vídeo'},
    'crunchyroll': { displayName: 'Crunchyroll', plataforma: 'Streaming de Vídeo'},
    'paramount+': { displayName: 'Paramount+', plataforma: 'Streaming de Vídeo'},
    'apple tv+': { displayName: 'Apple TV+', plataforma: 'Streaming de Vídeo'},
    'globoplay': { displayName: 'Globoplay', plataforma: 'Streaming de Vídeo'},
    'chatgpt': { displayName: 'ChatGPT', plataforma: 'Produtividade'},
    'google one': { displayName: 'Google One', plataforma: 'Armazenamento'},
    'microsoft 365': { displayName: 'Microsoft 365', plataforma: 'Produtividade'},
    'adobe': { displayName: 'Adobe Creative Cloud', plataforma: 'Produtividade'},
    'canva': { displayName: 'Canva Pro', plataforma: 'Design'},
    'r7': { displayName: 'R7 Play', plataforma: 'Streaming de Vídeo'},
    'deezer': { displayName: 'Deezer', plataforma: 'Streaming de Música'}
};
const availableServices = computed(() => Object.keys(platformStyles).map(key => ({ key, ...platformStyles[key] })).sort((a, b) => a.displayName.localeCompare(b.displayName)));

const handleServiceSelection = (formRef) => {
  const serviceData = availableServices.value.find(s => s.displayName === formRef.nomeStreamer);
  if (serviceData) {
    formRef.plataforma = serviceData.plataforma;
  }
};

const fetchAssinatura = async () => {
  if (!isEditing.value) return;
  try {
    const response = await axios.get(`${API_BASE_URL}/assinaturas-compartilhadas/${route.params.uuid}`, authConfig());
    const data = response.data;
    form.value = {
      nomeStreamer: data.nomeStreamer ?? '',
      plataforma: data.plataforma ?? '',
      valorMensal: data.valorMensal ?? 0,
      diaVencimento: data.diaVencimento ?? 1,
      emailsMembros: ''
    };
  } catch {
    alert('Erro ao carregar dados da assinatura.');
    router.push('/user/assinaturas-compartilhadas');
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    let response;
    if (isEditing.value) {
       // eslint-disable-next-line no-unused-vars
      const { emailsMembros, ...editPayload } = form.value; 
      response = await axios.put(`${API_BASE_URL}/assinaturas-compartilhadas/${route.params.uuid}`, editPayload, authConfig());
      router.push({ name: 'AssinaturasCompartilhadasUser', params: { id: route.params.uuid } });
    } else {
      const payload = { ...form.value, emailsMembros: (form.value.emailsMembros || '').split(/[\s,]+/).filter(Boolean) };
      response = await axios.post(`${API_BASE_URL}/assinaturas-compartilhadas`, payload, authConfig());
       if (response.data?.uuid) {
         router.push({ name: 'AssinaturasCompartilhadasUser', params: { id: response.data.uuid } });
       } else {
         router.push('/user/assinaturas-compartilhadas');
       }
    }
  } catch (error) {
    alert(error.response?.data?.message || `Erro ao ${isEditing.value ? 'salvar alterações' : 'criar grupo'}.`);
  } finally {
    isSubmitting.value = false;
  }
};

const goBack = () => {
  if (isEditing.value && route.params.uuid) {
     router.push({ name: 'AssinaturasCompartilhadasUser', params: { id: route.params.uuid } });
  } else {
     router.push('/user/assinaturas-compartilhadas');
  }
};

onMounted(() => {
  if (isEditing.value) {
    fetchAssinatura();
  }
});
</script>

<style scoped>
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20;
}
.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}
.form-input,
.form-select {
  @apply w-full px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 bg-slate-100/80 dark:bg-slate-800/70 rounded-xl transition ease-in-out ring-1 ring-transparent focus:outline-none focus:ring-2 focus:ring-[#5A189A] focus:bg-white dark:focus:bg-slate-900;
}
.form-select { @apply appearance-none; }
.form-label {
  @apply block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5;
}
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; transform: scale(0.98) translateY(-4px); }
</style>